"use client"
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import SkeletonShowcase from '../../ui/skeleton-showcase';
import Testimonials from '../home/testimonials';

const About = () => {
    return (
        <>
            <div className="relative min-h-screen font-jakarta flex flex-col items-center justify-start pt-32 overflow-hidden" style={{ background: 'linear-gradient(to bottom, #0052D4, #4364F7, #6FB1FC)' }}>
                {/* Background Grid Pattern */}
                <div className="absolute inset-0 z-0 mix-blend-overlay opacity-[0.1]"
                    style={{
                        backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.5) 1px, transparent 1px)',
                        backgroundSize: '40px 40px'
                    }}
                />

                {/* Header Content */}
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-block relative px-8 py-3 mb-8">
                        {/* Dashed Border Box Simulation - White for contrast */}
                        <div className="absolute inset-0 border border-dashed border-white/40 rounded-lg pointer-events-none" />

                        {/* Corners Squares - White */}
                        <div className="absolute -top-1 -left-1 w-2 h-2 bg-white" />
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-white" />
                        <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-white" />
                        <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-white" />

                        <h1 className="font-syne font-bold text-lg md:text-xl text-white tracking-widest uppercase mb-0">
                            About US
                        </h1>
                    </div>

                    <h1 className="font-syne font-bold text-4xl md:text-5xl lg:text-5xl text-white leading-[1.1] mb-8 tracking-tight drop-shadow-sm">
                        <span className="relative inline-block mr-2 px-1">
                            <motion.span
                                className="relative z-10"
                                animate={{ color: ["#ffffff", "#2563EB"] }}
                                transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                            >
                                AI-Powered
                            </motion.span>
                            <motion.span
                                className="absolute inset-0 bg-white -skew-x-6 rounded-md"
                                initial={{ scaleX: 0 }}
                                animate={{ scaleX: 1 }}
                                transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                                style={{ originX: 0, zIndex: 0 }}
                            />
                        </span> Business <br className="hidden md:block" />
                        Platform for Presentations, <br className="hidden md:block" />
                        Documents & Deep Research
                    </h1>

                    {/* Subheading */}
                    <p className="text-lg md:text-md text-blue-50 max-w-3xl mx-auto font-medium leading-relaxed drop-shadow-sm">
                        From presentations and pitch decks to business reports, market research, and cap tables — Deckwale helps professionals, teams, and businesses communicate ideas with clarity, confidence, and speed.
                    </p>
                </div>

                {/* Scroll Down Indicator */}
                <motion.div
                    className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20 cursor-pointer"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1, duration: 0.8 }}
                    onClick={() => {
                        const missionSection = document.getElementById('mission');
                        if (missionSection) {
                            missionSection.scrollIntoView({ behavior: 'smooth' });
                        }
                    }}
                >
                    <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 animate-bounce">
                        <ChevronDown size={24} strokeWidth={2} />
                    </div>
                </motion.div>

                {/* Skeleton Showcase (Replacing Clouds) */}
                <SkeletonShowcase />
            </div>

            {/* Mission Section - Creative/Mixed Media Style */}
            <div id="mission" className="relative w-full bg-white z-20 py-24 px-6">
                <div className="max-w-6xl mx-auto">
                    {/* Section Label */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="flex items-center gap-3 mb-10"
                    >
                        <div className="relative">
                            <span className="font-syne font-bold text-sm tracking-widest uppercase text-blue-600">Mission</span>
                        </div>
                        <div className="h-px w-20 bg-blue-100" />
                    </motion.div>

                    {/* Mission Statement */}
                    <div className="relative">
                        <motion.h2
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            className="font-syne font-bold text-4xl md:text-5xl lg:text-[42px] leading-[1.15] text-slate-900"
                        >
                            We believe <br className="hidden lg:block" />

                            <span className="relative inline-block mx-2">
                                <span className="relative z-10">strong ideas</span>
                                {/* Doodle: Underline Scribble */}
                                <motion.svg
                                    className="absolute -bottom-2 left-0 w-full h-4 text-blue-500"
                                    viewBox="0 0 100 10"
                                    fill="none"
                                    initial={{ pathLength: 0 }}
                                    whileInView={{ pathLength: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 1, delay: 0.5 }}
                                >
                                    <path d="M0 5 Q 50 10, 100 5" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                                </motion.svg>
                            </span>

                            deserve <br className="hidden lg:block" />

                            <span className="relative inline-block mx-2 px-3 rotate-[-1deg]">
                                <span className="relative z-10 text-white">strong communication tools</span>
                                {/* Doodle: Marker Highlight */}
                                <motion.span
                                    className="absolute inset-0 bg-purple-500 rounded-lg -skew-y-1"
                                    initial={{ scaleX: 0 }}
                                    whileInView={{ scaleX: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8, delay: 0.8 }}
                                    style={{ originX: 0, zIndex: 0 }}
                                />
                            </span>

                            — and strong connections. Deckwale exists to remove friction from professional communication so anyone can

                            <span className="relative inline-block mx-2">
                                <span className="relative z-10">focus</span>
                                {/* Doodle: Circle/Oval */}
                                <svg className="absolute -inset-2 w-[120%] h-[130%] -left-[10%] text-green-400 -z-10" viewBox="0 0 100 60" fill="none">
                                    <motion.path
                                        d="M10 30 Q 50 5, 90 30 T 10 30"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        initial={{ pathLength: 0 }}
                                        whileInView={{ pathLength: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 1, delay: 1.2 }}
                                    />
                                </svg>
                            </span>

                            on <span className="relative inline-block">
                                <span className="text-blue-600 italic">growth</span>
                                {/* Doodle: Curved Arrow pointing to growth */}
                                <motion.svg
                                    className="absolute -bottom-8 -right-8 w-12 h-12 text-orange-400"
                                    viewBox="0 0 50 50"
                                    fill="none"
                                    initial={{ pathLength: 0, opacity: 0 }}
                                    whileInView={{ pathLength: 1, opacity: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8, delay: 1.5 }}
                                >
                                    <path d="M40 10 Q 45 25, 10 35 L 20 25 M 10 35 L 22 42" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </motion.svg>
                            </span> and <span className="text-blue-600 italic">impact</span>.
                        </motion.h2>

                        {/* Floating Decorative Elements */}
                        <motion.div
                            className="absolute -right-6 -top-6 text-pink-300 hidden lg:block"
                            initial={{ opacity: 0, scale: 0 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 }}
                        >
                            {/* Doodle: Squiggle/Spring */}
                            <svg width="80" height="80" viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                                <path d="M10 50 Q 20 10, 30 50 T 50 50 T 70 50 T 90 50" />
                            </svg>
                        </motion.div>


                    </div>
                </div>
            </div>

            {/* Our Story Section - Professional Design Tool Aesthetic */}
            <div className="relative w-full py-32 overflow-hidden bg-slate-50">
                {/* Background Grid - Blueprint Look */}
                <div className="absolute inset-0 opacity-[0.3]"
                    style={{ backgroundImage: 'radial-gradient(#CBD5E1 1px, transparent 1px)', backgroundSize: '32px 32px', background: '#b5cdf9' }}
                />

                <div className="max-w-5xl mx-auto px-6 relative z-10">

                    {/* The "Selected Layer" Card */}
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 30 }}
                        whileInView={{ scale: 1, opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className="relative bg-white/95 backdrop-blur-md rounded-2xl p-8 md:p-16 shadow-[0_0_50px_-12px_rgba(37,99,235,0.25)] border border-blue-100"
                    >
                        {/* Selection Ring (active state) */}
                        <div className="absolute -inset-[2px] border-2 border-[#2563EB] rounded-2xl pointer-events-none opacity-80" />

                        {/* Selection Handles (Design Tool UI) - Crisp Blue */}
                        <div className="absolute -top-1.5 -left-1.5 w-3 h-3 bg-white border-2 border-[#2563EB] shadow-sm z-10 ring-1 ring-white" />
                        <div className="absolute -top-1.5 -right-1.5 w-3 h-3 bg-white border-2 border-[#2563EB] shadow-sm z-10 ring-1 ring-white" />
                        <div className="absolute -bottom-1.5 -left-1.5 w-3 h-3 bg-white border-2 border-[#2563EB] shadow-sm z-10 ring-1 ring-white" />
                        <div className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-white border-2 border-[#2563EB] shadow-sm z-10 ring-1 ring-white" />

                        {/* Layer Label - Creative Badge */}
                        <div className="absolute -top-10 left-0 flex items-center gap-2">
                            <div className="bg-[#2563EB] text-white text-[11px] font-bold px-3 py-1.5 rounded-lg font-mono tracking-wide shadow-lg shadow-blue-500/20 flex items-center gap-2 transform -rotate-2">
                                <span className="text-blue-200">✨ Layer:</span> Our_Story.tsx
                            </div>
                        </div>

                        <div className="space-y-8 text-center md:text-left relative z-10">
                            <h3 className="font-syne font-bold text-4xl md:text-5xl text-slate-900 tracking-tight leading-tight">
                                Built by <span className="text-[#2563EB] relative inline-block">
                                    Founders
                                    <svg className="absolute w-full h-3 -bottom-2 left-0 text-[#2563EB] opacity-40" viewBox="0 0 100 10" preserveAspectRatio="none">
                                        <path d="M0 5 Q 50 12 100 5" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round" />
                                    </svg>
                                </span>
                            </h3>

                            <div className="space-y-6 text-lg md:text-xl text-slate-600 leading-relaxed font-medium max-w-3xl">
                                <p>
                                    Deckwale was born inside <strong className="text-slate-900 font-bold">Seedicon</strong> — a platform connecting founders with investors.
                                </p>

                                <p>
                                    We realized that great ideas were getting lost in poorly designed documents — whether it was a founder's pitch, a consultant's proposal, a marketer's strategy deck, or a researcher's report. People were spending more time on formatting than on their actual work. So we built <strong className="text-[#2563EB]">Deckwale</strong>: an AI platform that lets anyone create professional presentations, documents, and research — so you can focus on your ideas, not the design.
                                </p>
                            </div>
                        </div>

                        {/* Collaborative Cursors - Professional Look */}

                        {/* Cursor 1: Designer */}
                        <motion.div
                            className="absolute bottom-10 right-10 md:right-32 z-20 pointer-events-none"
                            initial={{ x: 100, y: 100, opacity: 0 }}
                            whileInView={{ x: 0, y: 0, opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.6, duration: 1, type: "spring", stiffness: 100 }}
                        >
                            <motion.div
                                animate={{ x: [0, -40, 0], y: [0, 20, 0] }}
                                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                            >
                                {/* High Fidelity Cursor SVG */}
                                <svg width="35" height="35" viewBox="0 0 35 35" fill="none" style={{ filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.15))' }}>
                                    <path d="M12.5 4.5L25 29.5L18.5 29.5L15 34.5L10 34.5L13.5 29.5L6 29.5L12.5 4.5Z" fill="#A855F7" stroke="white" strokeWidth="2" strokeLinejoin="round" />
                                </svg>
                                <div className="ml-5 mt-[-10px] px-3 py-1 bg-[#A855F7] text-white text-[11px] font-bold rounded-full rounded-tl-none shadow-md whitespace-nowrap border border-white/20">
                                    Designer
                                </div>
                            </motion.div>
                        </motion.div>

                        {/* Cursor 2: Developer */}
                        <motion.div
                            className="absolute top-20 right-4 md:-right-8 z-20 pointer-events-none"
                            initial={{ x: 50, y: -50, opacity: 0 }}
                            whileInView={{ x: 0, y: 0, opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.9, duration: 1, type: "spring", stiffness: 100 }}
                        >
                            <motion.div
                                animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
                                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                            >
                                <svg width="35" height="35" viewBox="0 0 35 35" fill="none" style={{ filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.15))' }}>
                                    <path d="M12.5 4.5L25 29.5L18.5 29.5L15 34.5L10 34.5L13.5 29.5L6 29.5L12.5 4.5Z" fill="#14B8A6" stroke="white" strokeWidth="2" strokeLinejoin="round" />
                                </svg>
                                <div className="ml-5 mt-[-10px] px-3 py-1 bg-[#14B8A6] text-white text-[11px] font-bold rounded-full rounded-tl-none shadow-md whitespace-nowrap border border-white/20">
                                    Developer
                                </div>
                            </motion.div>
                        </motion.div>

                    </motion.div>

                    {/* Collaborative Badge - Pulse Effect */}
                    <motion.div
                        className="absolute -top-6 right-10 md:right-20 hidden lg:flex z-20 bg-white items-center gap-2 px-4 py-2.5 rounded-full shadow-xl shadow-blue-900/5 border border-slate-100"
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 1.2 }}
                    >
                        <div className="flex -space-x-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#A855F7] to-indigo-500 ring-2 ring-white" />
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#14B8A6] to-emerald-500 ring-2 ring-white" />
                            <div className="w-8 h-8 rounded-full bg-slate-100 ring-2 ring-white flex items-center justify-center text-[10px] font-bold text-slate-500">+4</div>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-xs font-bold text-slate-700 leading-none">Collaborating</span>
                            <span className="text-[10px] text-green-500 font-bold leading-none mt-0.5 flex items-center gap-1">
                                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" /> Active now
                            </span>
                        </div>
                    </motion.div>

                </div>
            </div>

            {/* Creative Metrics Section - Scribble & Condensed Style */}
            <div className="relative w-full py-16 bg-white">
                <div className="max-w-6xl mx-auto px-6">

                    {/* Section Header */}
                    <div className="text-center mb-12">
                        <span className="text-[#D946EF] font-bold tracking-wider text-xs uppercase mb-2 block">Our Impact</span>
                        <h2 className="text-3xl md:text-4xl font-syne font-bold text-slate-900">
                            Empowering Professionals Worldwide
                        </h2>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">

                        {/* Metric 1: Users */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            className="relative group p-8 rounded-2xl bg-[#ffffff] border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300"
                        >
                            <h3 className="font-syne font-bold text-4xl md:text-5xl text-slate-900 mb-1 relative inline-block">
                                50k+
                                <svg className="absolute -bottom-2 left-0 w-full h-3 text-[#D946EF] -z-10" viewBox="0 0 100 10" fill="none">
                                    <motion.path
                                        d="M0 5 Q 50 12 100 5"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                        strokeLinecap="round"
                                        initial={{ pathLength: 0 }}
                                        whileInView={{ pathLength: 1 }}
                                        transition={{ duration: 0.8, delay: 0.5 }}
                                    />
                                </svg>
                            </h3>
                            <p className="text-slate-500 font-medium text-sm">Active Users</p>
                        </motion.div>

                        {/* Metric 2: Decks */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 }}
                            className="relative group p-8 rounded-2xl bg-[#ffffff] border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300"
                        >
                            <h3 className="font-syne font-bold text-4xl md:text-5xl text-slate-900 mb-1 relative inline-block">
                                120k
                                {/* Doodle: Circle */}
                                <svg className="absolute -inset-4 w-[140%] h-[140%] -left-[20%] text-[#D946EF] -z-10 opacity-70" viewBox="0 0 100 60" fill="none">
                                    <motion.path
                                        d="M30 10 Q 80 0 90 30 T 20 50 T 30 10"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        initial={{ pathLength: 0 }}
                                        whileInView={{ pathLength: 1 }}
                                        transition={{ duration: 1, delay: 0.7 }}
                                    />
                                </svg>
                            </h3>
                            <p className="text-slate-500 font-medium text-sm">Decks Generated</p>
                        </motion.div>

                        {/* Metric 3: Money Raised */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.3 }}
                            className="relative group p-8 rounded-2xl bg-[#ffffff] border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300"
                        >
                            <h3 className="font-syne font-bold text-4xl md:text-5xl text-slate-900 mb-1 relative inline-block">
                                $200M
                                {/* Doodle: Underline */}
                                <svg className="absolute -bottom-1 left-0 w-full h-2 text-[#D946EF] opacity-50" viewBox="0 0 100 10" fill="none">
                                    <motion.path
                                        d="M0 5 L 100 5"
                                        stroke="currentColor"
                                        strokeWidth="6"
                                        strokeLinecap="round"
                                        initial={{ pathLength: 0 }}
                                        whileInView={{ pathLength: 1 }}
                                        transition={{ duration: 0.6, delay: 0.8 }}
                                    />
                                </svg>
                            </h3>
                            <p className="text-slate-500 font-medium text-sm">Raised by Users</p>
                        </motion.div>

                        {/* Metric 4: Reviews */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.4 }}
                            className="relative group p-8 rounded-2xl bg-[#ffffff] border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300"
                        >
                            <h3 className="font-syne font-bold text-4xl md:text-5xl text-slate-900 mb-1 relative inline-block">
                                4.9/5
                                <svg className="absolute -top-3 -right-6 w-8 h-8 text-[#D946EF] transform rotate-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <motion.path
                                        d="M20 6L9 17l-5-5"
                                        initial={{ pathLength: 0, opacity: 0 }}
                                        whileInView={{ pathLength: 1, opacity: 1 }}
                                        transition={{ duration: 0.5, delay: 1 }}
                                    />
                                </svg>
                            </h3>
                            <p className="text-slate-500 font-medium text-sm">Average Rating</p>
                        </motion.div>

                    </div>
                </div>
            </div>

            {/* Testimonials Section */}
            <Testimonials />
        </>
    );
};

export default About;
